import * as P from '@radix-ui/react-popover';
import { type ComponentPropsWithoutRef, createContext, type ReactNode, useContext, useState } from 'react';

import { useLayer } from './layers.ts';
import './ui.css';

const State = createContext<{ open: boolean; close: () => void } | null>(null);

/**
 * A small card that pops from a button (the Feathers ⓘ, the bell on desktop): Radix
 * Popover, so the trigger says it's expanded and what it controls, Escape and a tap
 * outside close it, and focus goes back to the trigger. Controlled (`open`,
 * `onOpenChange`) or not.
 *
 *   <Popover><PopoverTrigger className="fx-btn">Feathers…</PopoverTrigger>
 *     <PopoverContent>…</PopoverContent></Popover>
 */
export function Popover({
	open,
	defaultOpen = false,
	onOpenChange,
	children,
}: {
	open?: boolean | undefined;
	defaultOpen?: boolean | undefined;
	onOpenChange?: ((open: boolean) => void) | undefined;
	children: ReactNode;
}) {
	const [own, setOwn] = useState(defaultOpen);
	const shown = open ?? own;
	const set = (next: boolean) => {
		if (open === undefined) setOwn(next);
		onOpenChange?.(next);
	};
	return (
		<State.Provider value={{ open: shown, close: () => set(false) }}>
			<P.Root open={shown} onOpenChange={set}>
				{children}
			</P.Root>
		</State.Provider>
	);
}

/** The button that opens it (a real `<button>`; `asChild` to use your own). */
export const PopoverTrigger = P.Trigger;

/** Where it hangs from, when that's not the trigger. */
export const PopoverAnchor = P.Anchor;

/**
 * The card (ui.css `.ui-pop`: stock on an ink edge, the printed popover). It hangs under its
 * trigger, right edges aligned, and flips or slides to stay on screen (Radix places it with
 * `position: fixed`, so no column clips it either way). Portalled to the body by default;
 * `inline` keeps it where it's written, straight after its trigger in the page and inside its
 * landmark, for a card that's read as part of the page (the Feathers ⓘ note). `className`
 * adds to the look.
 */
export function PopoverContent({
	className,
	children,
	side = 'bottom',
	align = 'end',
	sideOffset = 2,
	inline = false,
	...props
}: ComponentPropsWithoutRef<typeof P.Content> & {
	/** Render in place, not at the end of the body. */
	inline?: boolean | undefined;
}) {
	const state = useContext(State);
	useLayer(!!state?.open, () => state?.close());
	const content = (
		<P.Content
			className={className ? `ui-pop ${className}` : 'ui-pop'}
			side={side}
			align={align}
			sideOffset={sideOffset}
			collisionPadding={16}
			{...props}
			onEscapeKeyDown={(e) => {
				// Nothing under it (a sheet, the game's keys) hears this Escape.
				e.stopPropagation();
				props.onEscapeKeyDown?.(e);
			}}
		>
			{children}
		</P.Content>
	);
	return inline ? content : <P.Portal>{content}</P.Portal>;
}
